import { PETTY_CASH_VOUCHER_STATUS_LABEL, PETTY_CASH_RECEIPT_STATUS_LABEL } from "../pettyCashStatusMaps";

// Shared by the Voucher Register page and the Petty Cash Overview (2026-09-15)
// so both list, total and export vouchers the same way.

export const money = (n) => Number(n || 0).toFixed(2);

// What the voucher really cost the fund: paid less any change handed back
// (Batch168 returns). Null while it is not paid yet.
export const voucherNet = (r) => (r.paidAmount == null ? null : Number(r.paidAmount) - Number(r.returnedAmount || 0));

export const payeeName = (r) => (r.paidToEmployeeName || r.paidToPersonName || "").trim();

// One payee = one employee (by RecordGuid), or one outside person by the name
// typed on the voucher - an outsider has no id, so their name is all there is.
export const payeeKey = (r) =>
  r.paidToEmployeeRecordGuid
    ? `e:${r.paidToEmployeeRecordGuid}`
    : r.paidToPersonName
    ? `p:${r.paidToPersonName.trim().toLowerCase()}`
    : "";

export const voucherTotals = (rows) => ({
  count: rows.length,
  requested: rows.reduce((s, r) => s + Number(r.requestedAmount || 0), 0),
  paid: rows.reduce((s, r) => s + Number(r.paidAmount || 0), 0),
  returned: rows.reduce((s, r) => s + Number(r.returnedAmount || 0), 0),
});

export const VOUCHER_REGISTER_EXPORT_COLUMNS = [
  { header: "Voucher", value: (r) => r.voucherNumber },
  { header: "Date", value: (r) => (r.expenseDate ? new Date(r.expenseDate).toLocaleDateString() : "-") },
  { header: "Branch", value: (r) => r.branchCode },
  { header: "Category", value: (r) => r.categoryName || "-" },
  { header: "Description", value: (r) => r.description },
  { header: "Requested", value: (r) => money(r.requestedAmount), align: "right" },
  { header: "Paid", value: (r) => (r.paidAmount == null ? "-" : money(r.paidAmount)), align: "right" },
  { header: "Returned", value: (r) => (Number(r.returnedAmount || 0) > 0 ? money(r.returnedAmount) : ""), align: "right" },
  { header: "Net", value: (r) => (voucherNet(r) == null ? "-" : money(voucherNet(r))), align: "right" },
  { header: "Paid To", value: (r) => payeeName(r) || "-" },
  { header: "Status", value: (r) => PETTY_CASH_VOUCHER_STATUS_LABEL[r.status] || r.status },
  {
    header: "Receipt",
    value: (r) => (r.receiptStatus && r.receiptStatus !== "NOT_REQUIRED" ? PETTY_CASH_RECEIPT_STATUS_LABEL[r.receiptStatus] : ""),
  },
];

export const SUMMARY_GROUPS = [
  { value: "category", label: "Category" },
  { value: "payee", label: "Paid To" },
  { value: "fund", label: "Fund" },
  { value: "branch", label: "Branch" },
  { value: "month", label: "Month" },
];

const monthKey = (d) => String(d || "").slice(0, 7);
const monthLabel = (key) =>
  key ? new Date(`${key}-01T00:00:00`).toLocaleDateString("en-GB", { month: "short", year: "numeric" }) : "-";

const groupOf = (r, groupBy) => {
  switch (groupBy) {
    case "payee": {
      const key = payeeKey(r);
      return { key: key || "none", label: payeeName(r) || "(no payee recorded)", filterValue: key || null };
    }
    case "fund":
      return { key: String(r.fundId ?? "none"), label: r.fundName || "(no fund)", filterValue: r.fundId ?? null };
    case "branch":
      return { key: String(r.branchId), label: r.branchCode, filterValue: null };
    case "month": {
      const key = monthKey(r.expenseDate);
      return { key, label: monthLabel(key), filterValue: null };
    }
    default:
      return { key: String(r.categoryId ?? "none"), label: r.categoryName || "(no category)", filterValue: r.categoryId ?? null };
  }
};

/**
 * Grouped voucher totals, worked out in the browser from the vouchers the
 * page already has - so it honours every filter the register does (amount
 * range, paid to, status, search), which the server summary
 * (sp_app_pettycash_voucher_summary) cannot. Same figures as that summary:
 * Requested over every voucher in the group, Paid only over PAID vouchers,
 * Returned against the voucher's own group.
 */
export const summarizeVouchers = (rows, groupBy) => {
  const map = new Map();
  rows.forEach((r) => {
    const g = groupOf(r, groupBy);
    if (!map.has(g.key)) {
      map.set(g.key, { ...g, totalCount: 0, totalAmount: 0, paidCount: 0, paidAmount: 0, returnedAmount: 0 });
    }
    const s = map.get(g.key);
    s.totalCount += 1;
    s.totalAmount += Number(r.requestedAmount || 0);
    if (r.status === "PAID") {
      s.paidCount += 1;
      s.paidAmount += Number(r.paidAmount || 0);
    }
    s.returnedAmount += Number(r.returnedAmount || 0);
  });
  const list = [...map.values()].map((s) => ({ ...s, net: s.paidAmount - s.returnedAmount }));
  return groupBy === "month"
    ? list.sort((a, b) => a.key.localeCompare(b.key))
    : list.sort((a, b) => b.net - a.net || b.totalAmount - a.totalAmount || a.label.localeCompare(b.label));
};
